import { Facebook, Instagram, Mail, MapPin, Phone } from "lucide-react";

import HelpPage from "@/components/dashboard/help-page";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/vendor/support")({
	component: RouteComponent
});

function RouteComponent() {
	return (
		<div className="flex flex-col min-h-screen">
			<header className="flex h-auto flex-col py-2">
				<Tabs defaultValue="help" className="w-full">
					<TabsList className="flex h-auto w-full justify-start gap-6 rounded-none border-b bg-transparent p-0">
						<TabsTrigger
							value="help"
							className="rounded-none border-b-2 border-transparent px-0 pb-4 pt-2 font-medium text-muted-foreground data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
						>
							Help Center
						</TabsTrigger>
						<TabsTrigger
							value="contact"
							className="rounded-none border-b-2 border-transparent px-0 pb-4 pt-2 font-medium text-muted-foreground data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
						>
							Contact
						</TabsTrigger>
						<TabsTrigger
							value="terms"
							className="rounded-none border-b-2 border-transparent px-0 pb-4 pt-2 font-medium text-muted-foreground data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none"
						>
							Terms and Policies
						</TabsTrigger>
					</TabsList>

					<TabsContent value="help">
						<HelpPage />
					</TabsContent>
					<TabsContent value="contact">
						<ContactPage />
					</TabsContent>
					<TabsContent value="terms">
						<TermsAndPolicies />
					</TabsContent>
				</Tabs>
			</header>
		</div>
	);
}

function ContactPage() {
	return (
		<div className="min-h-[50vh] bg-gradient-to-b from-[#EAE8FF] to-white px-4 py-10 rounded-lg">
			<div className="container mx-auto max-w-6xl space-y-2">
				{/* Header Section */}
				<div className="text-center space-y-2 mb-6">
					<h1 className="text-4xl md:text-5xl font-semibold text-[#5547D7]">
						Get in touch with us
					</h1>
					<p className="text-gray-600 max-w-3xl mx-auto text-lg">
						We make sure that our users receive immediate and necessary
						assistance while using Intasida. Feel free to use our call center,
						or any means of contact to reach out to us.
					</p>
				</div>

				{/* Contact Card */}
				<Card className="p-4 grid md:grid-cols-[400px_1fr] gap-8 shadow-lg">
					{/* Contact Information */}
					<div className="bg-gradient-to-br from-[#3e33b7] to-[#6b60d1] text-white p-8 rounded-lg space-y-8">
						<div className="space-y-4">
							<h2 className="text-2xl font-semibold">Contact Information</h2>
							<p className="text-gray-200">
								Contact our call center, or any means of contact to reach out to
								us.
							</p>
						</div>

						<div className="space-y-6">
							<div className="space-y-2">
								<div className="flex items-center gap-3">
									<Phone className="h-5 w-5" />
									<span>+234 801 234 5678</span>
								</div>
							</div>

							<div className="flex items-center gap-3">
								<Mail className="h-5 w-5" />
								<span>support@intasidatechnologies.com</span>
							</div>

							<div className="flex items-center gap-3">
								<MapPin className="h-5 w-5" />
								<span>Enugu, Nigeria</span>
							</div>
						</div>
					</div>

					{/* Contact Form */}
					<form className="space-y-2 py-2">
						<div className="grid md:grid-cols-2 gap-6">
							<div className="space-y-2">
								<label
									htmlFor="name"
									className="text-sm font-medium text-gray-700"
								>
									Name
									{" "}
									<span className="text-red-500">*</span>
								</label>
								<Input id="name" placeholder="Enter your name" required />
							</div>
							<div className="space-y-2">
								<label
									htmlFor="email"
									className="text-sm font-medium text-gray-700"
								>
									Email
									{" "}
									<span className="text-red-500">*</span>
								</label>
								<Input
									id="email"
									type="email"
									placeholder="Enter your email address"
									required
								/>
							</div>
						</div>

						<div className="space-y-2">
							<label
								htmlFor="subject"
								className="text-sm font-medium text-gray-700"
							>
								Subject
								{" "}
								<span className="text-red-500">*</span>
							</label>
							<Input id="subject" placeholder="Enter subject" required />
						</div>

						<div className="space-y-2">
							<label
								htmlFor="message"
								className="text-sm font-medium text-gray-700"
							>
								Message
								{" "}
								<span className="text-red-500">*</span>
							</label>
							<Textarea
								id="message"
								placeholder="Enter message"
								className="min-h-[150px] resize-none"
								required
							/>
						</div>

						<Button type="submit">Send message</Button>
					</form>
				</Card>

				{/* Footer */}
				<footer className="flex flex-col md:flex-row justify-between items-center pt-12 gap-6">
					<img
						src="/logo-with-color-black.png"
						alt="Intasida Logo"
						width={150}
						height={40}
						className="h-10 w-auto"
					/>

					<div className="flex items-center gap-6">
						<a
							href="/privacy-policy"
							className="text-gray-600 hover:text-[#5547D7] text-muted-foreground"
						>
							Privacy policy
						</a>
						<div className="flex items-center gap-4">
							<Button size="icon" variant="accent" className="shadow-none p-1">
								<Instagram
									className="h-6 w-6"
									fill="#4538C8"
									stroke="#EBE8FF"
								/>
							</Button>
							<Button size="icon" variant="accent" className="shadow-none">
								<Facebook className="h-6 w-6" fill="#4538C8" stroke="none" />
							</Button>
							<Button size="icon" variant="accent" className="shadow-none">
								<svg
									className="h-6 w-6"
									xmlns="http://www.w3.org/2000/svg"
									width="1200"
									height="1227"
									fill="none"
									viewBox="0 0 1200 1227"
								>
									<path
										fill="#4538C8"
										d="M714.163 519.284 1160.89 0h-105.86L667.137 450.887 357.328 0H0l468.492 681.821L0 1226.37h105.866l409.625-476.152 327.181 476.152H1200L714.137 519.284h.026ZM569.165 687.828l-47.468-67.894-377.686-540.24h162.604l304.797 435.991 47.468 67.894 396.2 566.721H892.476L569.165 687.854v-.026Z"
									/>
								</svg>
							</Button>
						</div>
					</div>
				</footer>
			</div>
		</div>
	);
}

function TermsAndPolicies() {
	return (
		<section className="space-y-4">
			<div className="h-[70vh] w-full bg-white rounded-lg p-6 text-sm overflow-y-auto">
				<h2 className="text-xl font-bold mb-4">TERMS AND CONDITIONS</h2>
				<ol className="list-decimal pl-6 space-y-4">
					<li>
						<h3 className="font-semibold">Introduction</h3>
						<p>
							Welcome to Intasida, a digital platform that connects online
							vendors and buyers, facilitating seamless transactions between
							both parties. By accessing and using our website, you agree to be
							bound by these Terms and Conditions. If you do not agree with any
							part of these Terms, please discontinue use of our services
							immediately.
						</p>
					</li>
					<li>
						<h3 className="font-semibold">User Agreement</h3>
						<p>
							By using this website, you confirm that you have read, understood,
							and agreed to abide by these Terms and Conditions. You also
							consent to the collection, processing, and storage of personal
							data provided on the platform in accordance with our Privacy
							Policy.
						</p>
					</li>
					<li>
						<h3 className="font-semibold">Personal Data Processing</h3>
						<p>
							By using Intasida, you acknowledge and consent to the collection
							and processing of your personal data, including but not limited to
							your name, contact details, and transaction history. This
							information will be used to facilitate transactions, enhance user
							experience, and comply with regulatory requirements. For more
							details on how we handle your personal data, please refer to our
							Privacy Policy.
						</p>
					</li>
					<li>
						<h3 className="font-semibold">Role of Intasida</h3>
						<p>
							Intasida acts as an intermediary between online vendors and
							buyers. We do not own, manufacture, or directly sell any goods
							listed on our platform. Any disputes concerning products purchased
							must be resolved between the vendor and the buyer, subject to our
							dispute resolution process.
						</p>
					</li>
					<li>
						<h3 className="font-semibold">Forfeiture of Unclaimed Goods</h3>
						<p>
							If a buyer purchases goods but fails to claim them within a
							reasonable period of time as determined by Intasida, such goods
							shall be deemed forfeited. Intasida reserves the right to dispose
							of or take any other appropriate action regarding such unclaimed
							goods at its sole discretion.
						</p>
					</li>
					<li>
						<h3 className="font-semibold">Payments and Refunds</h3>
						<p>
							All transactions conducted through Intasida must comply with the
							payment policies stated on our platform. Refunds, if applicable,
							will be processed according to the vendor's refund policy and
							subject to any applicable regulations.
						</p>
					</li>
					<li>
						<h3 className="font-semibold">Prohibited Activities</h3>
						<p>
							Users are prohibited from engaging in fraudulent activities,
							misrepresentations, or any unlawful conduct while using Intasida's
							services. Any violations may result in account suspension, legal
							action, or permanent ban from the platform.
						</p>
					</li>
					<li>
						<h3 className="font-semibold">Modifications to Terms</h3>
						<p>
							Intasida reserves the right to amend these Terms and Conditions at
							any time. Continued use of the website following any changes
							constitutes acceptance of the revised Terms.
						</p>
					</li>
					<li>
						<h3 className="font-semibold">Governing Law</h3>
						<p>
							These Terms and Conditions shall be governed by and interpreted in
							accordance with the laws of the Federal Republic of Nigeria.
						</p>
					</li>
					<li>
						<h3 className="font-semibold">Contact Information</h3>
						<p>
							For inquiries regarding these Terms and Conditions, please contact
							us at ….
						</p>
					</li>
				</ol>
			</div>

			<div className="flex justify-end max-md:justify-center gap-4">
				<Button>Agree with our terms</Button>
				<Button variant="accent">Decline</Button>
			</div>
		</section>
	);
}
